import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import useBetaWorkspaceToggle from 'pages/ProjectsPage/WorkspacePage/components/WorkspaceRightPane/hooks/useBetaWorkspaceToggle'
import React, { forwardRef } from 'react'
import ReactMarkdown, { Components } from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import rehypeSanitize from 'rehype-sanitize'
import remarkGfm from 'remark-gfm'
import { useChatProfileStore } from './ChatProfileStore'
import OpenInNewTabLinkRenderer from './OpenInNewTabLinkRenderer'
import {
    chatLinkColor,
    chatLinkColorBeta,
    chatPrimaryColor,
    chatPrimaryColorBeta,
    chatSuggestionBgColor,
    chatSuggestionBgColorBeta,
    chatSuggestionTextColor,
    chatUserBorderColor,
} from './colors'
import { Message } from './message'

interface MessageContentStylesProps {
    isBetaWorkspaceToggleEnabled?: boolean
}

const useStyles = makeStyles((theme) => ({
    /** Markdown Renderer has some default styles we need to overwrite to adhere to design */
    markdownContent: {
        width: '100%',
        overflowX: 'auto', // Add H-Scrollbar when content overflows (usually for tables)
        padding: '1px',
        userSelect: 'text', // Allow text selection
        '& a': {
            color: (props: MessageContentStylesProps) =>
                props.isBetaWorkspaceToggleEnabled
                    ? chatLinkColorBeta
                    : chatLinkColor, // Change links color
            textDecoration: 'underline', // Add underline to links
        },
        '& p': {
            textWrap: 'wrap',
            wordBreak: 'break-word',
            margin: '0px', // remove default margins
            marginBottom: '8px',
        },
        '& table': {
            marginBottom: '8px',
        },
        '& ul': {
            paddingInlineStart: theme.spacing(3), // reduce bullet/lists indentation
        },
        '& *': {
            fontSize: pxToRem(12), // Set font size of all element to 12px
        },
        '& h2': {
            fontSize: pxToRem(14),
            fontWeight: 600,
            marginTop: '10px',
            marginBottom: '4px',
        },
        '& h3': {
            fontSize: pxToRem(13),
            fontWeight: 600,
            marginTop: '8px',
            marginBottom: '4px',
        },
        '& strong, & b': {
            fontWeight: 600,
        },
        '& td, & th': {
            // change the border color of the tables
            border: '1px solid #C0C0C0',
        },
    },
    messageContent: {
        fontFamily: (props: MessageContentStylesProps) =>
            props.isBetaWorkspaceToggleEnabled ? 'Lexend' : 'Roboto',
        fontSize: pxToRem(12),
        fontWeight: 400,
        padding: theme.spacing(1.25),
    },
    agentMessageContent: {
        backgroundColor: (props: MessageContentStylesProps) =>
            props.isBetaWorkspaceToggleEnabled
                ? chatPrimaryColorBeta
                : chatPrimaryColor,
        color: (props: MessageContentStylesProps) =>
            props.isBetaWorkspaceToggleEnabled
                ? '#31353D'
                : theme.palette.common.white,
        borderRadius: '0px 8px 8px 8px',
    },
    userMessageContent: {
        backgroundColor: theme.palette.common.white,
        color: 'black',
        border: `1px solid`,
        borderColor: chatUserBorderColor,
        borderRadius: '8px 0px 8px 8px',
    },
    suggestedMessageContent: {
        backgroundColor: (props: MessageContentStylesProps) =>
            props.isBetaWorkspaceToggleEnabled
                ? chatSuggestionBgColorBeta
                : chatSuggestionBgColor,
        color: chatSuggestionTextColor,
        borderRadius: '8px 0px 8px 8px',
    },
    suggestedText: {
        color: ' #2B42E2',
        fontWeight: 500,
        fontSize: pxToRem(12),
        paddingBottom: pxToRem(5),
    },
}))

export interface MessageContentProps {
    message: Message
    isFirstSuggested?: boolean
}

const MessageContent = forwardRef<HTMLDivElement, MessageContentProps>(
    ({ message, isFirstSuggested }, ref) => {
        const isBetaWorkspaceToggleEnabled = useBetaWorkspaceToggle()
        const classes = useStyles({ isBetaWorkspaceToggleEnabled })
        const chatUser = useChatProfileStore((state) => state.chatUser)

        // Apply transformations to change display of message
        const localMessage = React.useMemo(() => {
            if (message.isGreeting) {
                // Replace {user} with the user's name
                return {
                    ...message,
                    content: message.content.replace(
                        '{user}',
                        chatUser?.profileInfo?.firstName ?? 'there'
                    ),
                }
            }

            return message
        }, [message, chatUser])

        // Custom components for markdown renderer
        const customMarkdownComponents: Components = React.useMemo(
            () => ({
                a: ({ node, ...props }) => (
                    <OpenInNewTabLinkRenderer {...props} />
                ),
            }),
            []
        )

        return (
            <>
                {isBetaWorkspaceToggleEnabled &&
                    localMessage.role === 'suggested' &&
                    isFirstSuggested && (
                        <div className={classes.suggestedText}>Suggested:</div>
                    )}
                <div
                    ref={ref}
                    className={clsx(classes.messageContent, {
                        [classes.agentMessageContent]:
                            localMessage.role === 'agent',
                        [classes.userMessageContent]:
                            localMessage.role === 'user',
                        [classes.suggestedMessageContent]:
                            localMessage.role === 'suggested',
                    })}
                    aria-label={`${localMessage.role} message content`}
                >
                    <ReactMarkdown
                        className={classes.markdownContent}
                        components={customMarkdownComponents}
                        remarkPlugins={[remarkGfm]} // Allows rendering of GitHub Flavored Markdown (e.g. tables)
                        rehypePlugins={[
                            rehypeRaw /* Allows rendering of HTML content*/,
                            [
                                rehypeSanitize /* Sanitizes HTML content before rendering */,
                            ],
                        ]}
                    >
                        {localMessage.content}
                    </ReactMarkdown>
                </div>
            </>
        )
    }
)

export default MessageContent
